import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Search, HelpCircle } from 'lucide-react';

const faqs = [
  {
    category: 'General',
    questions: [
      {
        question: 'What services does Square Trigger offer?',
        answer: 'Square Trigger offers a comprehensive suite of digital services including website development, custom tool creation, SEO services, social media marketing, UI/UX design, maintenance & support, and e-commerce solutions.'
      },
      {
        question: 'Where is Square Trigger located?',
        answer: 'We\'re based in Canada but work with clients globally. Our digital-first approach allows us to collaborate effectively regardless of location.'
      },
      {
        question: 'How do I get started with Square Trigger?',
        answer: 'Simply fill out our Get Started form or contact us directly. We\'ll schedule a discovery call to understand your needs and create a tailored proposal.'
      }
    ]
  },
  {
    category: 'Projects & Pricing',
    questions: [
      {
        question: 'How long does it take to build a website?',
        answer: 'Project timelines vary based on complexity. A standard business website takes 4-8 weeks, while complex applications may take 8-16 weeks.'
      },
      {
        question: 'What is your pricing structure?',
        answer: 'Our pricing is project-based and tailored to your specific requirements. We provide detailed quotes after understanding your needs during our discovery phase.'
      },
      {
        question: 'Do you offer payment plans?',
        answer: 'Yes, we offer flexible payment options including milestone-based payments to make our services accessible to businesses of all sizes.'
      }
    ]
  },
  {
    category: 'Technical',
    questions: [
      {
        question: 'What technologies do you use?',
        answer: 'We work with modern technologies including React, Next.js, WordPress, Shopify, and custom solutions. We recommend the best platform based on your specific needs.'
      },
      {
        question: 'Will my website be mobile-friendly?',
        answer: 'Absolutely. All our websites are built with a mobile-first approach, ensuring optimal display and functionality across all devices.'
      },
      {
        question: 'Can I update the website content myself?',
        answer: 'Yes, we build websites with user-friendly content management systems (CMS) that allow you to easily update content without technical knowledge.'
      }
    ]
  },
  {
    category: 'Support',
    questions: [
      {
        question: 'Do you provide ongoing support?',
        answer: 'Yes, we provide 30 days of post-launch support included and offer ongoing maintenance packages for continued support.'
      },
      {
        question: 'What if something breaks after launch?',
        answer: 'Our post-launch support covers bug fixes and issues. For ongoing maintenance, we offer support packages that include regular updates and emergency support.'
      },
      {
        question: 'How can I contact support?',
        answer: 'You can reach our support team via email at info@squaretrigger.com or through our contact form. Clients on maintenance plans have access to priority support.'
      }
    ]
  }
];

export default function FAQsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [openIndex, setOpenIndex] = useState(null);

  const categories = ['All', ...faqs.map(f => f.category)];

  const filteredFAQs = faqs.flatMap(category => 
    category.questions
      .filter(q => 
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .filter(() => activeCategory === 'All' || category.category === activeCategory)
      .map(q => ({ ...q, category: category.category }))
  );

  return (
    <Layout>
    <SEO
  title="Digital Agency FAQs: Answers to Top Questions | Square Trigger"
  description="Find answers to the most frequently asked questions about our digital agency's complete services, pricing, and more. Read more."
  keywords={SEO_KEYWORDS.faqs}
/>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <HelpCircle className="h-4 w-4 mr-2" />
              FAQs
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              Frequently Asked{' '}
              <span className="gradient-text">Questions</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Find answers to common questions about our services, process, and how we can help your business grow.
            </p>

            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-border">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto space-y-4">
            {filteredFAQs.length === 0 ? (
              <div className="text-center py-12">
                <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No matching questions found.</p>
              </div>
            ) : (
              filteredFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-card rounded-xl border border-border overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-accent/50 transition-colors"
                  >
                    <div className="pr-8">
                      <Badge variant="outline" className="mb-2 text-xs">
                        {faq.category}
                      </Badge>
                      <span className="font-semibold text-foreground block">{faq.question}</span>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 text-muted-foreground leading-relaxed">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
